/**
 * BlocksPanel — the Blocks palette (Faz 5.8, OCS block_palette.rs parity):
 * tabs Current Drawing / Recent / Favorites (+ the Libraries placeholder),
 * wildcard search (`*` `?`), live wire thumbnails, click-to-place and
 * drag-and-drop INSERT, the insert-options strip (Scale / Rotation / Repeat /
 * Explode) and the right-click menu (Insert / Favorites / Edit Block /
 * Rename / Delete with the dependent-INSERT warning).
 */

import {
    Box,
    Button,
    Checkbox,
    Flex,
    HStack,
    IconButton,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    NumberInput,
    NumberInputField,
    Text,
    Tooltip,
    VStack,
} from '@chakra-ui/react';
import { Grid, List, Star, StarOff } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '../../state/store';
import { useBlockStore } from '../../state/commandAuxStores';
import { BlockWirePreview } from './BlockWirePreview';
import {
    readFavoriteBlocks, readRecentBlocks, toggleFavoriteBlock,
} from './blockPalettePrefs';
import { onBlockDragStart } from './blockPlacement';
import { ConfirmDialog } from '../common/ConfirmDialog';

type PaletteTab = 'current' | 'recent' | 'favorites';

/** OCS "Filter... (* ? wildcards)" — glob to RegExp (case-insensitive). */
function wildcardToRegExp(pattern: string): RegExp | null {
    const trimmed = pattern.trim();
    if (!trimmed) return null;
    const escaped = trimmed
        .replace(/[.+^${}()|[\]\\]/g, '\\$&')
        .replace(/\*/g, '.*')
        .replace(/\?/g, '.');
    try {
        return new RegExp(`^${escaped}$`, 'i');
    } catch {
        return null;
    }
}

export function BlocksPanel() {
    const { t } = useTranslation();
    const defs = useBlockStore((s) => s.defs);
    const removeDef = useBlockStore((s) => s.remove);
    const entities = useAppStore((s) => s.entities);

    const [tab, setTab] = useState<PaletteTab>('current');
    const [search, setSearch] = useState('');
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
    // Insert options strip (OCS BlockInsertOptions parity; the render motor
    // scales references uniformly, so X/Y wait for per-axis support).
    const [scale, setScale] = useState(1);
    const [rotation, setRotation] = useState(0);
    const [repeat, setRepeat] = useState(false);
    const [explode, setExplode] = useState(false);
    // Right-click menu state + dialogs.
    const [menu, setMenu] = useState<{ x: number; y: number; name: string } | null>(null);
    const [renameFrom, setRenameFrom] = useState<string | null>(null);
    const [renameTo, setRenameTo] = useState('');
    const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
    // Bumped after favorites/recent writes so the lists re-read.
    const [prefsVersion, setPrefsVersion] = useState(0);

    const dependentCount = useCallback((name: string) => {
        let count = 0;
        Object.values(entities).forEach((e) => {
            if (e.type === 'insert' && String((e.data as Record<string, unknown>).blockName ?? '') === name) count++;
        });
        return count;
    }, [entities]);

    const names = useMemo(() => {
        let base: string[];
        if (tab === 'recent') {
            base = readRecentBlocks();
        } else if (tab === 'favorites') {
            base = readFavoriteBlocks();
        } else {
            base = Object.keys(defs).sort((a, b) => a.localeCompare(b));
        }
        // Resolve against the live defs: stale recent/favorite names drop.
        const live = base.filter((n) => !!defs[n]);
        const re = wildcardToRegExp(search);
        return re ? live.filter((n) => re.test(n)) : live;
        // prefsVersion re-reads the localStorage-backed lists after a toggle.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defs, tab, search, prefsVersion]);

    const insertFlags = useMemo(() => {
        const flags: string[] = [String(scale), String(rotation)];
        if (explode) flags.push('explode');
        if (repeat) flags.push('repeat');
        return flags.join(' ');
    }, [scale, rotation, explode, repeat]);

    const insert = useCallback((name: string) => {
        window.dispatchEvent(new CustomEvent('yap:run-command', {
            detail: { input: `INSERT ${name} ${insertFlags}`.trim() },
        }));
    }, [insertFlags]);

    const isFavorite = (name: string) => readFavoriteBlocks().includes(name);

    const closeMenu = () => setMenu(null);

    return (
        <Box h="full" display="flex" flexDirection="column" minW={0}>
            {/* Tabs (OCS: Current Drawing / Recent / Favorites / Libraries) */}
            <HStack spacing={0} px={2} pt={2} flexShrink={0} flexWrap="nowrap" overflowX="auto"
                sx={{ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
                {(['current', 'recent', 'favorites'] as PaletteTab[]).map((key) => (
                    <Box
                        key={key}
                        as="button"
                        role="tab"
                        aria-selected={tab === key}
                        onClick={() => setTab(key)}
                        px={2}
                        py={1}
                        fontSize="11px"
                        whiteSpace="nowrap"
                        flexShrink={0}
                        color={tab === key ? 'text.primary' : 'text.secondary'}
                        borderBottom="2px solid"
                        borderColor={tab === key ? 'brand.500' : 'transparent'}
                        _hover={{ color: 'text.primary' }}
                    >
                        {t(`ui.cad.blocks.tab_${key}`)}
                    </Box>
                ))}
                <Box flex="1" />
                {/* Libraries placeholder: content lands with the Faz 9 .dwt
                    work — visible but inert (spec: hidden-button first shot). */}
                <Tooltip label={t('ui.cad.blocks.librariesSoon')} hasArrow>
                    <Box as="span" fontSize="11px" color="text.secondary" opacity={0.5} flexShrink={0} px={2} py={1}
                        whiteSpace="nowrap">
                        {t('ui.cad.blocks.tab_libraries')}
                    </Box>
                </Tooltip>
            </HStack>

            {/* Search + view mode */}
            <Flex px={2} py={1} gap={1} align="center" flexShrink={0} flexWrap="nowrap" minW={0}>
                <Input
                    size="xs"
                    variant="filled"
                    bg="surface.300"
                    color="text.primary"
                    placeholder={t('ui.cad.blocks.filter')}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    minW={0}
                    flex="1"
                />
                <IconButton
                    size="xs"
                    variant={viewMode === 'grid' ? 'solid' : 'ghost'}
                    aria-label={t('ui.cad.blocks.gridView')}
                    icon={<Grid size={12} />}
                    onClick={() => setViewMode('grid')}
                    flexShrink={0}
                />
                <IconButton
                    size="xs"
                    variant={viewMode === 'list' ? 'solid' : 'ghost'}
                    aria-label={t('ui.cad.blocks.listView')}
                    icon={<List size={12} />}
                    onClick={() => setViewMode('list')}
                    flexShrink={0}
                />
            </Flex>

            {/* Tiles (lazy: only the active tab's list mounts previews) */}
            <Box flex="1" minH="0" overflowY="auto" overflowX="hidden" px={2} pb={2}>
                {names.length === 0 ? (
                    <Text fontSize="xs" color="text.secondary" py={4} textAlign="center">
                        {t('ui.cad.blocks.empty')}
                    </Text>
                ) : viewMode === 'grid' ? (
                    <Flex flexWrap="wrap" gap={2} pt={1}>
                        {names.map((name) => (
                            <Box
                                key={name}
                                role="button"
                                aria-label={`${t('ui.cad.blocks.insert')} ${name}`}
                                draggable
                                onDragStart={(e: React.DragEvent) => onBlockDragStart(e, name)}
                                onClick={() => insert(name)}
                                onContextMenu={(e: React.MouseEvent) => {
                                    e.preventDefault();
                                    setMenu({ x: e.clientX, y: e.clientY, name });
                                }}
                                w="88px"
                                p={1}
                                borderRadius="4px"
                                border="1px solid"
                                borderColor="border.base"
                                cursor="grab"
                                _hover={{ borderColor: 'border.hover', bg: 'bg.hover' }}
                            >
                                <BlockWirePreview def={defs[name]} size={80} />
                                <Text fontSize="10px" color="text.primary" noOfLines={1} mt={1} title={name}>
                                    {name}
                                </Text>
                            </Box>
                        ))}
                    </Flex>
                ) : (
                    <VStack spacing={0} align="stretch" pt={1}>
                        {names.map((name) => (
                            <Flex
                                key={name}
                                role="button"
                                draggable
                                onDragStart={(e: React.DragEvent) => onBlockDragStart(e, name)}
                                onClick={() => insert(name)}
                                onContextMenu={(e: React.MouseEvent) => {
                                    e.preventDefault();
                                    setMenu({ x: e.clientX, y: e.clientY, name });
                                }}
                                align="center"
                                gap={2}
                                px={1}
                                py={1}
                                borderRadius="4px"
                                cursor="grab"
                                _hover={{ bg: 'bg.hover' }}
                            >
                                <BlockWirePreview def={defs[name]} size={28} />
                                <Text fontSize="xs" color="text.primary" noOfLines={1} minW={0} flex="1">
                                    {name}
                                </Text>
                                <Text fontSize="10px" color="text.secondary" flexShrink={0}>
                                    {t('ui.cad.blocks.componentCount', { n: defs[name]?.entities.length ?? 0 })}
                                </Text>
                            </Flex>
                        ))}
                    </VStack>
                )}
            </Box>

            {/* Insert options strip (OCS BlockInsertOptions parity) */}
            <Flex
                px={2}
                py={2}
                gap={2}
                align="center"
                borderTop="1px solid"
                borderColor="border.base"
                flexShrink={0}
                flexWrap="nowrap"
                overflowX="auto"
                minH="34px"
                sx={{ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}
            >
                <Text fontSize="10px" color="text.secondary" flexShrink={0}>{t('ui.cad.blocks.scale')}</Text>
                <NumberInput size="xs" min={0.0001} value={scale} onChange={(_, n) => setScale(n > 0 ? n : 1)} w="64px" flexShrink={0}>
                    <NumberInputField bg="surface.300" color="text.primary" fontSize="xs" />
                </NumberInput>
                <Text fontSize="10px" color="text.secondary" flexShrink={0}>{t('ui.cad.blocks.rotation')}</Text>
                <NumberInput size="xs" value={rotation} onChange={(_, n) => setRotation(Number.isFinite(n) ? n : 0)} w="64px" flexShrink={0}>
                    <NumberInputField bg="surface.300" color="text.primary" fontSize="xs" />
                </NumberInput>
                <Checkbox size="sm" isChecked={repeat} onChange={(e) => setRepeat(e.target.checked)} flexShrink={0}>
                    <Text fontSize="10px" color="text.primary" whiteSpace="nowrap">{t('ui.cad.blocks.repeat')}</Text>
                </Checkbox>
                <Checkbox size="sm" isChecked={explode} onChange={(e) => setExplode(e.target.checked)} flexShrink={0}>
                    <Text fontSize="10px" color="text.primary" whiteSpace="nowrap">{t('ui.cad.blocks.explode')}</Text>
                </Checkbox>
            </Flex>

            {/* Right-click menu (panel-local; closes on backdrop click) */}
            {menu && (
                <>
                    <Box position="fixed" inset={0} zIndex={40} onClick={closeMenu} onContextMenu={(e) => { e.preventDefault(); closeMenu(); }} />
                    <Box
                        position="fixed"
                        left={`${menu.x}px`}
                        top={`${menu.y}px`}
                        zIndex={41}
                        bg="bg.panel"
                        border="1px solid"
                        borderColor="border.base"
                        borderRadius="6px"
                        boxShadow="lg"
                        py={1}
                        minW="170px"
                        w="max-content"
                    >
                        {([
                            { key: 'insert', run: () => insert(menu.name) },
                            {
                                key: isFavorite(menu.name) ? 'removeFavorite' : 'addFavorite',
                                run: () => { toggleFavoriteBlock(menu.name); setPrefsVersion((v) => v + 1); },
                            },
                            { key: 'editBlock', run: () => window.dispatchEvent(new CustomEvent('yap:run-command', { detail: { input: `BEDIT ${menu.name}` } })) },
                            { key: 'rename', run: () => { setRenameFrom(menu.name); setRenameTo(menu.name); } },
                            { key: 'delete', run: () => setDeleteTarget(menu.name) },
                        ] as const).map((item) => (
                            <Flex
                                key={item.key}
                                as="button"
                                px={3}
                                py={1.5}
                                w="full"
                                align="center"
                                gap={2}
                                fontSize="xs"
                                color={item.key === 'delete' ? 'red.400' : 'text.primary'}
                                whiteSpace="nowrap"
                                _hover={{ bg: 'bg.hover' }}
                                onClick={() => { closeMenu(); item.run(); }}
                            >
                                {item.key === 'addFavorite' && <Star size={12} />}
                                {item.key === 'removeFavorite' && <StarOff size={12} />}
                                {t(`ui.cad.blocks.menu_${item.key}`)}
                            </Flex>
                        ))}
                    </Box>
                </>
            )}

            {/* Rename dialog */}
            <Modal isOpen={renameFrom !== null} onClose={() => setRenameFrom(null)} size="xs">
                <ModalOverlay />
                <ModalContent bg="bg.panel">
                    <ModalHeader fontSize="sm">{t('ui.cad.blocks.renameTitle')}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Input
                            size="sm"
                            variant="filled"
                            bg="surface.300"
                            color="text.primary"
                            value={renameTo}
                            onChange={(e) => setRenameTo(e.target.value)}
                            autoFocus
                        />
                    </ModalBody>
                    <ModalFooter>
                        <Button size="sm" variant="ghost" mr={2} onClick={() => setRenameFrom(null)}>
                            {t('common.cancel')}
                        </Button>
                        <Button
                            size="sm"
                            colorScheme="blue"
                            isDisabled={!renameTo.trim() || (!!defs[renameTo.trim()] && renameTo.trim() !== renameFrom)}
                            onClick={() => {
                                const block = useBlockStore.getState();
                                const from = renameFrom!;
                                const to = renameTo.trim();
                                const def = block.defs[from];
                                if (def && to) {
                                    block.define({ ...def, name: to });
                                    block.remove(from);
                                }
                                setRenameFrom(null);
                            }}
                        >
                            {t('common.ok')}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Delete confirmation (dependent INSERT warning) */}
            <ConfirmDialog
                isOpen={deleteTarget !== null}
                onClose={() => setDeleteTarget(null)}
                onConfirm={() => {
                    if (deleteTarget) removeDef(deleteTarget);
                    setDeleteTarget(null);
                }}
                title={t('ui.cad.blocks.deleteTitle', { name: deleteTarget ?? '' })}
                message={t('ui.cad.blocks.deleteMessage', { n: deleteTarget ? dependentCount(deleteTarget) : 0 })}
                confirmLabel={t('common.delete')}
                cancelLabel={t('common.cancel')}
                tone="destructive"
            />
        </Box>
    );
}
